import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import {
  enableWebNotifications,
  getWebNotificationPrefs,
  saveWebNotificationPrefs,
  webNotificationPermission,
} from '../services/notificationService';
import './NotificationSetupPrompt.css';

const choices = [
  ['messagesEnabled', 'Message alerts', 'Chats, replies, and offers so you do not miss people reaching out.'],
  ['tournamentsEnabled', 'Tournament alerts', 'Check-in reminders, bracket updates, announcements, and match-ready calls.'],
  ['teamsEnabled', 'Team alerts', 'Join requests, roster changes, promotions, bans, and team announcements.'],
  ['nearbyEnabled', 'Nearby suggestions', 'Use location when nearby players, rooms, and tournaments need it.'],
];

export default function NotificationSetupPrompt() {
  const { user, loading } = useAuth();
  const [show, setShow] = useState(false);
  const [prefs, setPrefs] = useState(getWebNotificationPrefs);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (loading || !user) return;
    const current = getWebNotificationPrefs();
    setPrefs(current);
    setShow(!current.setupSeen);
  }, [loading, user]);

  const save = async (requestPermission) => {
    if (!user) return;
    setBusy(true);
    setMessage('');
    try {
      await saveWebNotificationPrefs(user.uid, prefs);
      if (requestPermission && (prefs.messagesEnabled || prefs.tournamentsEnabled || prefs.teamsEnabled)) {
        await enableWebNotifications(user.uid);
      }
      setShow(false);
    } catch (error) {
      setMessage(error.message || 'Could not finish notification setup.');
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;
  const permission = webNotificationPermission();

  return (
    <Modal show centered onHide={() => save(false)} dialogClassName="p4r-alert-setup-dialog" contentClassName="p4r-alert-setup">
      <div className="p4r-sheet-handle" />
      <Modal.Body>
        <h2>Set up your alerts</h2>
        <p className="p4r-alert-intro">Choose the important stuff. Your browser controls the final notification permission.</p>
        <div className={`p4r-alert-permission ${permission === 'granted' ? 'ok' : ''}`}>
          <strong>{permission === 'granted' ? 'Browser notifications are on' : 'Browser permission is still required'}</strong>
          <span>{permission === 'denied' ? 'Notifications are blocked for this site. Change the permission in your browser site settings.' : 'You can change these choices later in Profile Editor → Account.'}</span>
        </div>
        <div className="p4r-alert-options">
          {choices.map(([key, title, body]) => (
            <label key={key}>
              <span><strong>{title}</strong><small>{body}</small></span>
              <Form.Check type="switch" checked={!!prefs[key]} onChange={(e) => setPrefs((p) => ({ ...p, [key]: e.target.checked }))} />
            </label>
          ))}
        </div>
        {message && <div className="p4r-alert-error">{message}</div>}
        <div className="p4r-alert-actions">
          <Button variant="outline-light" disabled={busy} onClick={() => save(false)}>Maybe later</Button>
          <Button disabled={busy} onClick={() => save(true)}>{busy ? 'Saving…' : 'Save and continue'}</Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
